// @flow

import * as React from 'react';
import cx from 'classnames';
import styles from './VenueInfo.module.scss';
import type { PreviewVenue, HighlightCapacityType } from './types';
import VenuePreviewPrice from './VenuePreviewPrice';
import Rating from 'common/components/Rating';
import impossible from 'common/types/impossible';
import VenueLink from './VenueLink';
import FontAwesomeIcon, {
  regularCalendarCheck,
  solidPerson,
  customSitting,
  solidEye,
  solidTriangleExclamation
} from 'common/components/FontAwesomeIcon.js';
import ReviewCount from './ReviewCount';

export type Props = {|
  venue: PreviewVenue,
  onClickVenueLink?: () => void,
  openInNewTab: boolean,
  highlightCapacity?: HighlightCapacityType,
  isActive?: boolean,
  children?: React.Node
|};

export default function VenueInfo(props: Props): React.Node {
  const {
    venue,
    onClickVenueLink,
    openInNewTab,
    highlightCapacity = 'BOTH',
    isActive = false,
    children
  } = props;

  return (
    <VenueLink
      venue={venue}
      openInNewTab={openInNewTab}
      onClickVenueLink={onClickVenueLink}
    >
      <VenueInfoContainer>
        <VenueTitle venue={venue} isActive={isActive} />
        <VenueCity city={venue.city} />
        <VenuePreviewPrice venue={venue} />
        <VenueDetails>
          <VenueDetailsLeft venue={venue} />
          <VenueDetailsRight
            venue={venue}
            highlightCapacity={highlightCapacity}
          />
        </VenueDetails>
        {children}
      </VenueInfoContainer>
    </VenueLink>
  );
}

function VenueInfoContainer(props: {|
  className?: string,
  children: React.Node
|}): React.Node {
  return (
    <div className={cx(styles.infoContainer, props.className)}>
      {props.children}
    </div>
  );
}

function VenueTitle(props: {|
  venue: PreviewVenue,
  isActive: boolean
|}): React.Node {
  return (
    <div
      className={cx(styles.venueTitle, {
        [styles['venueTitle--active']]: props.isActive
      })}
      data-testid="search-result-name"
    >
      <Warnings venue={props.venue} />
      {props.venue.title}
    </div>
  );
}

function VenueCity(props: { city: string | null, ... }): React.Node {
  const { city } = props;

  return <p>{city}</p>;
}

function VenueDetails(props: {| children: React.Node |}): React.Node {
  return <div className={styles.venueDetails}>{props.children}</div>;
}

function VenueDetailsLeft(props: {|
  venue: PreviewVenue,
  additionalItem?: React.Node
|}): React.Node {
  return (
    <span className={styles.venueDetailsLeft}>
      <Rating score={props.venue.reviews_average} />
      <ReviewCount reviewCount={props.venue.reviews_count} />
    </span>
  );
}

function VenueDetailsRight(props: {|
  venue: PreviewVenue,
  highlightCapacity: HighlightCapacityType
|}): React.Node {
  const { venue, highlightCapacity } = props;
  return (
    <span className={styles.venueDetailsRight}>
      <AvailabilityData hasAvailabilityData={venue.has_availability_data} />
      <CapacitySittingData
        capacity={venue.capacity_sitting}
        highlight={['SITTING', 'BOTH'].includes(highlightCapacity)}
      />
      <CapacityStandingData
        capacity={venue.capacity_standing}
        highlight={['STANDING', 'BOTH'].includes(highlightCapacity)}
      />
    </span>
  );
}

function AvailabilityData({
  hasAvailabilityData
}: {|
  hasAvailabilityData: boolean
|}) {
  if (!hasAvailabilityData) return null;

  return (
    <span
      className={styles.venueDetailsWithSeparator}
      title={__('venues.similar_venues.has_availability_data')}
    >
      <FontAwesomeIcon icon={regularCalendarCheck} />
    </span>
  );
}

type CapacityProps = {| capacity: number | null, highlight: boolean |};

function CapacitySittingData({ capacity, highlight }: CapacityProps) {
  return (
    <span
      className={cx(styles.venueDetailsWithSeparator, {
        [styles.muted]: !highlight
      })}
      title={__('venues.similar_venues.capacity_sitting')}
    >
      <FontAwesomeIcon className={styles.capacityIcon} icon={customSitting} />
      {capacity || '-'}
    </span>
  );
}

function CapacityStandingData({ capacity, highlight }: CapacityProps) {
  return (
    <span
      className={cx(styles.venueDetailsWithSeparator, {
        [styles.muted]: !highlight
      })}
      title={__('venues.similar_venues.capacity_standing')}
    >
      <FontAwesomeIcon className={styles.capacityIcon} icon={solidPerson} />
      {capacity || '-'}
    </span>
  );
}

function Warnings({ venue }: { venue: PreviewVenue, ... }) {
  if (!venue.warning) return null;

  switch (venue.warning) {
    case 'unpublished':
      return (
        <span data-testid="venue-warning-unpublished">
          <FontAwesomeIcon
            icon={solidTriangleExclamation}
            aria-hidden={false}
            className={styles.warningIcon}
            title={__('search.venue_preview.published')}
          />{' '}
        </span>
      );
    case 'restricted':
      return (
        <span data-testid="venue-warning-restricted">
          <FontAwesomeIcon
            icon={solidEye}
            aria-hidden={false}
            className={styles.warningIcon}
            title={__('search.venue_preview.restricted')}
          />{' '}
        </span>
      );
    default:
      return impossible(venue.warning);
  }
}
